/**
 * Onglet Manager : filtres et agrégations (logique pure, testée).
 *
 * Règles :
 * - la période porte sur la date de CRÉATION de la tâche ;
 * - les tâches en cours sont un état présent : leurs compteurs (en cours, plus ancienne en cours)
 *   ignorent la période et le filtre « commercial » (personne ne les a encore cochées) ;
 * - les délais (médianes) ne portent que sur les tâches TERMINÉES : une tâche annulée n'a pas de délai ;
 * - centre d'une tâche = assigned_center (équipe) ; une tâche « Moi » est rangée sous le centre du dossier
 *   pour le filtre, mais forme sa propre ligne dans le tableau par centre.
 */
import { isTaskOpen, median, taskDurationMs, type CaseTask, type CaseTaskType } from "./caseTasks";

/** Dossier joint à la tâche (sous-ensemble de `cases`). */
export interface ManagerTaskCase {
  id: string;
  title: string | null;
  company_name: string | null;
  client_first_name: string | null;
  client_last_name: string | null;
  center: string | null;
}

export interface ManagerTask extends CaseTask {
  case: ManagerTaskCase | null;
}

export const MANAGER_TASK_SELECT =
  "id,case_id,task_type,note,assigned_center,assigned_to,created_by,created_at,completed_at,completed_by,cancelled_at,cancelled_by," +
  "case:cases(id,title,company_name,client_first_name,client_last_name,center)";

export type ManagerPeriod = "7d" | "30d" | "90d" | "all";
export type ManagerStatusFilter = "open" | "completed" | "cancelled" | "all";
export type TaskStatus = "open" | "completed" | "cancelled";

export const MANAGER_PERIODS: { value: ManagerPeriod; label: string }[] = [
  { value: "7d", label: "7 j" },
  { value: "30d", label: "30 j" },
  { value: "90d", label: "90 j" },
  { value: "all", label: "Tout" },
];

export const MANAGER_STATUSES: { value: ManagerStatusFilter; label: string }[] = [
  { value: "open", label: "En cours" },
  { value: "completed", label: "Terminées" },
  { value: "cancelled", label: "Annulées" },
  { value: "all", label: "Toutes" },
];

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  open: "En cours",
  completed: "Terminée",
  cancelled: "Annulée",
};

export interface ManagerFilters {
  period: ManagerPeriod;
  /** null = tous les centres. */
  center: string | null;
  /** Qui a coché ; null = tout le monde. */
  completedBy: string | null;
  taskType: CaseTaskType | null;
  status: ManagerStatusFilter;
}

export const DEFAULT_MANAGER_FILTERS: ManagerFilters = {
  period: "30d",
  center: null,
  completedBy: null,
  taskType: null,
  status: "all",
};

/** Ligne du tableau par centre réservée aux tâches « Moi ». */
export const SELF_TASKS_ROW = "__self__";
export const SELF_TASKS_LABEL = "Tâches « Moi » (manager)";

const DAY_MS = 86_400_000;
const PERIOD_DAYS: Record<Exclude<ManagerPeriod, "all">, number> = { "7d": 7, "30d": 30, "90d": 90 };

/** Début de la période en ms (null = depuis toujours). */
export function periodStartMs(period: ManagerPeriod, now: number): number | null {
  return period === "all" ? null : now - PERIOD_DAYS[period] * DAY_MS;
}

/** ISO du début de période, pour la requête serveur. */
export function periodStartIso(period: ManagerPeriod, now: number): string | null {
  const start = periodStartMs(period, now);
  return start == null ? null : new Date(start).toISOString();
}

export function taskStatus(task: Pick<CaseTask, "completed_at" | "cancelled_at">): TaskStatus {
  if (task.completed_at) return "completed";
  if (task.cancelled_at) return "cancelled";
  return "open";
}

/** Centre d'une tâche pour le filtre : équipe assignée, sinon centre du dossier (tâche « Moi »). */
export function taskCenter(task: ManagerTask): string | null {
  return task.assigned_center ?? task.case?.center ?? null;
}

export function isInPeriod(task: Pick<CaseTask, "created_at">, period: ManagerPeriod, now: number): boolean {
  const start = periodStartMs(period, now);
  if (start == null) return true;
  const created = Date.parse(task.created_at);
  return Number.isFinite(created) && created >= start;
}

function matchesScope(task: ManagerTask, filters: Pick<ManagerFilters, "center" | "taskType">): boolean {
  if (filters.center && taskCenter(task) !== filters.center) return false;
  if (filters.taskType && task.task_type !== filters.taskType) return false;
  return true;
}

/** Liste détaillée : tous les filtres s'appliquent. */
export function filterTasks(tasks: readonly ManagerTask[], filters: ManagerFilters, now: number): ManagerTask[] {
  return tasks.filter((task) => {
    if (!isInPeriod(task, filters.period, now)) return false;
    if (!matchesScope(task, filters)) return false;
    if (filters.completedBy && task.completed_by !== filters.completedBy) return false;
    if (filters.status !== "all" && taskStatus(task) !== filters.status) return false;
    return true;
  });
}

/** Tâches en cours du périmètre (centre + type), toutes périodes confondues. */
export function openTasksInScope(tasks: readonly ManagerTask[], filters: ManagerFilters): ManagerTask[] {
  return tasks.filter((task) => isTaskOpen(task) && matchesScope(task, filters));
}

/** Tâches terminées du périmètre, créées dans la période, cochées par le commercial filtré. */
export function completedTasksInScope(
  tasks: readonly ManagerTask[],
  filters: ManagerFilters,
  now: number,
): ManagerTask[] {
  return tasks.filter(
    (task) =>
      !!task.completed_at &&
      isInPeriod(task, filters.period, now) &&
      matchesScope(task, filters) &&
      (!filters.completedBy || task.completed_by === filters.completedBy),
  );
}

function completionDurations(tasks: readonly CaseTask[]): number[] {
  return tasks
    .filter((task) => !!task.completed_at)
    .map((task) => taskDurationMs(task))
    .filter((value): value is number => value != null);
}

/** Délai médian création → coche (tâches terminées seulement ; annulées et en cours exclues). */
export function medianCompletionMs(tasks: readonly CaseTask[]): number | null {
  return median(completionDurations(tasks));
}

/** Âge de la plus ancienne tâche en cours (ms), null s'il n'y en a pas. */
export function oldestOpenAgeMs(tasks: readonly CaseTask[], now: number): number | null {
  let oldest: number | null = null;
  for (const task of tasks) {
    if (!isTaskOpen(task)) continue;
    const age = taskDurationMs(task, now);
    if (age != null && (oldest == null || age > oldest)) oldest = age;
  }
  return oldest;
}

export interface ManagerKpis {
  openCount: number;
  completedCount: number;
  medianCompletionMs: number | null;
  oldestOpenAgeMs: number | null;
  oldestOpenTask: ManagerTask | null;
}

export function computeKpis(tasks: readonly ManagerTask[], filters: ManagerFilters, now: number): ManagerKpis {
  const open = openTasksInScope(tasks, filters);
  const completed = completedTasksInScope(tasks, filters, now);
  let oldestOpenTask: ManagerTask | null = null;
  for (const task of open) {
    if (!oldestOpenTask || Date.parse(task.created_at) < Date.parse(oldestOpenTask.created_at)) oldestOpenTask = task;
  }
  return {
    openCount: open.length,
    completedCount: completed.length,
    medianCompletionMs: medianCompletionMs(completed),
    oldestOpenAgeMs: oldestOpenAgeMs(open, now),
    oldestOpenTask,
  };
}

export interface CenterStatsRow {
  /** Nom du centre, ou SELF_TASKS_ROW. */
  key: string;
  label: string;
  openCount: number;
  completedCount: number;
  medianCompletionMs: number | null;
  oldestOpenAgeMs: number | null;
}

/**
 * Tableau par centre. `centers` = centres à afficher même sans tâche (ordre conservé) ;
 * les centres rencontrés dans les tâches sont ajoutés à la suite, la ligne « Moi » en dernier.
 */
export function statsByCenter(
  tasks: readonly ManagerTask[],
  filters: ManagerFilters,
  now: number,
  centers: readonly string[] = [],
): CenterStatsRow[] {
  const open = openTasksInScope(tasks, filters);
  const completed = completedTasksInScope(tasks, filters, now);
  const rowKey = (task: ManagerTask) => (task.assigned_to ? SELF_TASKS_ROW : (task.assigned_center ?? ""));

  const keys: string[] = filters.center ? [filters.center] : [...centers];
  for (const task of [...open, ...completed]) {
    const key = rowKey(task);
    if (key && key !== SELF_TASKS_ROW && !keys.includes(key)) keys.push(key);
  }
  if ([...open, ...completed].some((task) => rowKey(task) === SELF_TASKS_ROW)) keys.push(SELF_TASKS_ROW);

  return keys.map((key) => {
    const rowOpen = open.filter((task) => rowKey(task) === key);
    const rowCompleted = completed.filter((task) => rowKey(task) === key);
    return {
      key,
      label: key === SELF_TASKS_ROW ? SELF_TASKS_LABEL : key,
      openCount: rowOpen.length,
      completedCount: rowCompleted.length,
      medianCompletionMs: medianCompletionMs(rowCompleted),
      oldestOpenAgeMs: oldestOpenAgeMs(rowOpen, now),
    };
  });
}

export interface CommercialStatsRow {
  profileId: string;
  completedCount: number;
  medianCompletionMs: number | null;
  /** Centres de la personne (dans le périmètre). */
  centers: string[];
  /** Tâches d'équipe terminées de ses centres sur la période (dénominateur de la part). */
  teamCompletedCount: number;
  /** Part des tâches de ses centres cochées par elle (0–1), null si aucune tâche terminée dans ses centres. */
  shareOfTeam: number | null;
}

/**
 * Tableau par commercial : toutes les personnes membres d'un centre du périmètre, plus celles qui ont coché
 * au moins une tâche. Tri : tâches cochées décroissant, puis `nameOf`.
 */
export function statsByCommercial(
  tasks: readonly ManagerTask[],
  filters: ManagerFilters,
  now: number,
  members: readonly { center: string; profile_id: string }[],
  nameOf: (profileId: string) => string = (id) => id,
): CommercialStatsRow[] {
  // Le dénominateur ne dépend pas du commercial filtré.
  const completedAll = completedTasksInScope(tasks, { ...filters, completedBy: null }, now);
  const centersByProfile = new Map<string, string[]>();
  for (const member of members) {
    if (filters.center && member.center !== filters.center) continue;
    const list = centersByProfile.get(member.profile_id) ?? [];
    if (!list.includes(member.center)) list.push(member.center);
    centersByProfile.set(member.profile_id, list);
  }
  const ids = new Set<string>(centersByProfile.keys());
  for (const task of completedAll) if (task.completed_by) ids.add(task.completed_by);
  if (filters.completedBy) {
    for (const id of [...ids]) if (id !== filters.completedBy) ids.delete(id);
  }

  const rows = [...ids].map((profileId) => {
    const mine = completedAll.filter((task) => task.completed_by === profileId);
    const centers = (centersByProfile.get(profileId) ?? []).sort((a, b) => a.localeCompare(b, "fr"));
    const team = completedAll.filter((task) => !!task.assigned_center && centers.includes(task.assigned_center));
    const mineInTeam = team.filter((task) => task.completed_by === profileId).length;
    return {
      profileId,
      completedCount: mine.length,
      medianCompletionMs: medianCompletionMs(mine),
      centers,
      teamCompletedCount: team.length,
      shareOfTeam: team.length ? mineInTeam / team.length : null,
    };
  });
  return rows.sort(
    (a, b) => b.completedCount - a.completedCount || nameOf(a.profileId).localeCompare(nameOf(b.profileId), "fr"),
  );
}

/** Personnes ayant coché au moins une tâche (pour le filtre « commercial »). */
export function completerIds(tasks: readonly CaseTask[]): string[] {
  return [...new Set(tasks.map((task) => task.completed_by).filter((id): id is string => !!id))];
}

/** Libellé d'un dossier : entreprise, sinon client, sinon titre. */
export function caseDisplayName(caseRow: ManagerTaskCase | null): string {
  if (!caseRow) return "Dossier supprimé";
  const client = [caseRow.client_first_name, caseRow.client_last_name].filter(Boolean).join(" ").trim();
  return caseRow.company_name?.trim() || client || caseRow.title?.trim() || "Dossier sans nom";
}

/** Second libellé (client si l'entreprise est le titre principal, sinon titre). */
export function caseSecondaryName(caseRow: ManagerTaskCase | null): string | null {
  if (!caseRow) return null;
  const client = [caseRow.client_first_name, caseRow.client_last_name].filter(Boolean).join(" ").trim();
  const primary = caseDisplayName(caseRow);
  const candidate = caseRow.company_name?.trim() ? client || caseRow.title : caseRow.title;
  return candidate && candidate.trim() !== primary ? candidate.trim() : null;
}

// ─── Équipe ────────────────────────────────────────────────────────

/** Centres de la matrice : centres connus des marques, puis ceux déjà présents dans les équipes. */
export function teamMatrixCenters(knownCenters: readonly string[], memberCenters: readonly string[]): string[] {
  const result = [...new Set(knownCenters)];
  const extras = [...new Set(memberCenters)]
    .filter((center) => !result.includes(center))
    .sort((a, b) => a.localeCompare(b, "fr"));
  return [...result, ...extras];
}

/**
 * Centres dont l'équipe ne compte aucun commercial (membre non manager) : les tâches d'équipe créées
 * par le manager n'y seraient vues par personne (l'auteur ne voit pas ses propres tâches d'équipe).
 */
export function centersWithoutSales(
  centers: readonly string[],
  members: readonly { center: string; profile_id: string }[],
  isManagerProfile: (profileId: string) => boolean,
): string[] {
  return centers.filter(
    (center) => !members.some((member) => member.center === center && !isManagerProfile(member.profile_id)),
  );
}
